'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import AppLogo from '@/components/ui/AppLogo';
import { Search, Calendar, Home, ArrowRight, Shield, Award, Clock, ChevronRight, Phone, Star } from 'lucide-react';

const popularTests = [
'Complete Blood Count (CBC)',
'Lipid Profile',
'Thyroid Profile (T3, T4, TSH)',
'HbA1c (Diabetes)',
'Vitamin D & B12',
'Liver Function Test',
'Kidney Function Test',
'Complete Health Checkup'];


const trustBadges = [
{ icon: Shield, label: 'NABL Accredited', sub: 'MC-6214' },
{ icon: Award, label: 'ISO 15189:2022', sub: 'Certified Lab' },
{ icon: Clock, label: '24–48 Hr', sub: 'Report Delivery' }];


export default function HeroSection() {
  const [searchQuery, setSearchQuery] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);

  const filtered = popularTests?.filter((t) =>
  t?.toLowerCase()?.includes(searchQuery?.toLowerCase())
  );

  return (
    <section className="relative overflow-hidden min-h-[92vh] flex items-center">
      {/* Background healthcare image */}
      <div className="absolute inset-0 z-0">
        <Image
          src="https://img.rocket.new/generatedImages/rocket_gen_img_1ea2bb1c0-1767468203453.png"
          alt="Modern pathology laboratory with advanced diagnostic equipment and Indian medical professionals"
          fill
          className="object-cover object-center"
          priority />
        
        {/* Strong gradient overlay for clear text legibility */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#041530]/97 via-[#071d4a]/92 to-[#0c2d6b]/60" />
        {/* Extra dark layer on left for text area */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#020d1f]/70 via-[#020d1f]/30 to-transparent" />
        {/* Subtle vignette at bottom */}
        <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-[#041530]/80 to-transparent" />
        {/* Top fade */}
        <div className="absolute top-0 left-0 right-0 h-20 bg-gradient-to-b from-[#041530]/50 to-transparent" />
      </div>
      {/* Decorative elements */}
      <div className="absolute left-0 top-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl pointer-events-none z-0" />
      <div className="absolute right-1/3 bottom-1/4 w-64 h-64 bg-sky-400/8 rounded-full blur-3xl pointer-events-none z-0" />
      {/* Watermark logo */}
      <div className="absolute right-8 top-1/2 -translate-y-1/2 opacity-[0.04] pointer-events-none z-10 hidden xl:block">
        <AppLogo size={440} />
      </div>
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-10 2xl:px-16 w-full py-20 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left content */}
          <div>
            {/* Brand identity badge */}
            <div className="flex items-center gap-3 mb-7">
              <div className="bg-white/12 backdrop-blur-md border border-white/20 rounded-2xl px-4 py-2.5 flex items-center gap-3 shadow-lg">
                <AppLogo size={44} />
                <div>
                  <div className="text-white font-extrabold text-sm leading-tight tracking-wide">51 Plus Lab</div>
                  <div className="text-sky-300 text-xs font-semibold tracking-wider uppercase">NABL Accredited · Noida</div>
                </div>
              </div>
              <div className="inline-flex items-center gap-2 bg-emerald-500/20 border border-emerald-400/35 rounded-full px-3.5 py-1.5 shadow-sm">
                <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                <span className="text-emerald-300 text-xs font-bold tracking-wide">Open Now</span>
              </div>
            </div>

            {/* Main heading — larger, bolder, better contrast */}
            <h1 className="text-5xl xl:text-6xl 2xl:text-7xl font-extrabold text-white leading-[1.08] mb-5 tracking-tight">
              Noida&apos;s Most{' '}
              <span className="relative inline-block">
                <span className="text-sky-300">Trusted</span>
                <span className="absolute -bottom-1 left-0 right-0 h-0.5 bg-sky-400/50 rounded-full" />
              </span>
              <br />
              <span className="text-white/95">Diagnostic Lab</span>
            </h1>

            {/* Subheading — improved legibility */}
            <p className="text-white/85 text-lg xl:text-xl leading-relaxed mb-8 max-w-lg font-light">
              NABL-accredited pathology, advanced diagnostics, and corporate health checkups.{' '}
              <span className="text-sky-200 font-medium">Home sample collection</span> available across all Noida sectors.
            </p>

            {/* Trust indicators row */}
            <div className="flex items-center gap-4 mb-8">
              <div className="flex items-center gap-1.5">
                {[1,2,3,4,5]?.map(i => (
                  <Star key={i} size={13} className="text-amber-400 fill-amber-400" />
                ))}
              </div>
              <span className="text-white/70 text-sm font-medium">4.7 · 663+ Google Reviews</span>
              <span className="text-white/30">·</span>
              <span className="text-white/70 text-sm font-medium">CGHS Empanelled</span>
              <Image
                src="/assets/images/add-1784962598831.png"
                alt="ON PANEL LIVE - CGHS & CAPF Empanelled"
                width={120}
                height={60}
                className="object-contain h-12 w-auto"
              />
            </div>

            {/* Search bar — enhanced */}
            <div className="relative mb-7">
              <div className="flex items-center gap-3 bg-white rounded-2xl shadow-2xl overflow-visible border border-white/20">
                <div className="flex-1 flex items-center gap-2.5 px-5">
                  <Search size={17} className="text-slate-400 flex-shrink-0" />
                  <input
                    type="text"
                    placeholder="Search tests, packages, doctors..."
                    className="w-full py-4 text-slate-800 bg-transparent outline-none text-sm font-medium placeholder:text-slate-400"
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e?.target?.value);
                      setShowSuggestions(e?.target?.value?.length > 0);
                    }}
                    onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                    onFocus={() => setShowSuggestions(searchQuery?.length > 0)} />
                </div>
                <Link
                  href="/book-appointment"
                  className="btn-primary m-2 py-3 px-6 rounded-xl text-sm font-bold">
                  Search
                </Link>
              </div>
              {showSuggestions && filtered?.length > 0 &&
              <div className="absolute top-full left-0 right-0 mt-2 bg-card border border-border rounded-xl shadow-modal z-50 overflow-hidden">
                  {filtered?.slice(0, 5)?.map((test) =>
                <button
                  key={`suggest-${test}`}
                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-muted text-sm text-foreground transition-colors text-left"
                  onMouseDown={() => {
                    setSearchQuery(test);
                    setShowSuggestions(false);
                  }}>
                      <Search size={13} className="text-muted-foreground" />
                      {test}
                    </button>
                )}
                </div>
              }
            </div>

            {/* Popular tests */}
            <div className="mb-8">
              <p className="text-white/50 text-xs font-bold mb-3 uppercase tracking-widest">Popular Tests</p>
              <div className="flex flex-wrap gap-2">
                {popularTests?.slice(0, 5)?.map((test) =>
                <Link
                  key={`popular-${test}`}
                  href="/book-appointment"
                  className="px-3.5 py-1.5 bg-white/10 hover:bg-white/20 border border-white/20 hover:border-white/35 rounded-full text-white/85 hover:text-white text-xs font-medium transition-all duration-200">
                    {test}
                  </Link>
                )}
              </div>
            </div>

            {/* CTA buttons */}
            <div className="flex flex-wrap gap-3 items-center">
              <Link href="/book-appointment" className="btn-white shadow-xl">
                <Calendar size={16} />
                Book Appointment
              </Link>
              <Link href="#home-collection" className="btn-outline" style={{ borderColor: 'rgba(255,255,255,0.35)', color: 'white', background: 'rgba(255,255,255,0.08)' }}>
                <Home size={16} />
                Home Collection
              </Link>
              <a href="tel:+919818986535" className="flex items-center gap-1.5 text-white/70 hover:text-white text-sm font-semibold transition-colors py-2 ml-1">
                <Phone size={14} />
                +91 98189 86535 <ChevronRight size={13} />
              </a>
            </div>
          </div>

          {/* Right: Trust badges + quick booking card */}
          <div className="hidden lg:block">
            <div className="grid grid-cols-1 gap-4 max-w-sm ml-auto">
              {/* Quick book card — enhanced */}
              <div className="bg-white/[0.08] backdrop-blur-xl border border-white/15 rounded-3xl p-6 shadow-2xl">
                <div className="flex items-center gap-2.5 mb-5 pb-4 border-b border-white/10">
                  <AppLogo size={34} />
                  <div>
                    <h3 className="text-white font-extrabold text-base leading-tight">Quick Book</h3>
                    <p className="text-white/50 text-xs">Most popular tests</p>
                  </div>
                </div>
                <div className="space-y-2.5">
                  {[
                  { label: 'Complete Blood Count', price: '₹299', time: 'Report in 6 hrs', popular: true },
                  { label: 'Full Body Checkup', price: '₹1,499', time: 'Report in 24 hrs', popular: false },
                  { label: 'Thyroid Profile', price: '₹499', time: 'Report in 12 hrs', popular: false },
                  { label: 'Vitamin D + B12', price: '₹699', time: 'Report in 24 hrs', popular: false }]?.
                  map((item) =>
                  <Link
                    key={`quickbook-${item?.label}`}
                    href="/book-appointment"
                    className="flex items-center justify-between p-3.5 bg-white/[0.06] hover:bg-white/[0.14] border border-white/10 hover:border-white/20 rounded-2xl transition-all duration-200 group">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-white text-sm font-semibold">{item?.label}</span>
                          {item?.popular &&
                        <span className="badge-popular text-[10px]">HOT</span>
                        }
                        </div>
                        <span className="text-white/45 text-xs mt-0.5 block">{item?.time}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sky-300 font-extrabold text-sm">{item?.price}</span>
                        <ArrowRight size={13} className="text-white/30 group-hover:text-sky-300 group-hover:translate-x-0.5 transition-all" />
                      </div>
                    </Link>
                  )}
                </div>
                <Link
                  href="/health-packages"
                  className="mt-5 flex items-center justify-center gap-1.5 text-sky-300 hover:text-sky-200 text-sm font-bold transition-colors py-2 border-t border-white/10">
                  View All Packages <ArrowRight size={13} />
                </Link>
              </div>

              {/* Trust badges — enhanced */}
              <div className="grid grid-cols-3 gap-3">
                {trustBadges?.map((badge) =>
                <div
                  key={`trust-${badge?.label}`}
                  className="bg-white/[0.08] backdrop-blur-sm border border-white/15 rounded-2xl p-3.5 text-center hover:bg-white/[0.12] transition-colors">
                    <badge.icon size={20} className="text-sky-300 mx-auto mb-2" />
                    <div className="text-white text-xs font-bold leading-tight">{badge?.label}</div>
                    <div className="text-white/45 text-xs mt-0.5">{badge?.sub}</div>
                  </div>
                )}
                {/* ON PANEL LIVE badge */}
                <div className="bg-white/[0.08] backdrop-blur-sm border border-white/15 rounded-2xl p-2 text-center hover:bg-white/[0.12] transition-colors flex items-center justify-center">
                  <Image
                    src="/assets/images/add-1784962598831.png"
                    alt="ON PANEL LIVE - CGHS & CAPF Empanelled"
                    width={140}
                    height={90}
                    className="object-contain w-full h-auto"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );

}